// Commitment modal form body — FE-043c.
//
// Replaces the broken plain-UUID-text-input UX of the legacy
// ``CommitmentForm`` with the proper pickers the detail surface
// already uses (SearchableSelect for Account / Assignee / Reviewer
// / Tags / Plan Year). The shape is intentionally a *single*
// vertical list of fields tuned for the modal frame; the legacy
// ``CommitmentForm`` keeps the page two-column layout for the
// /new fallback route.
//
// Submit is owned by the parent modal — this component is purely
// the controlled-input form body.

import { useContext, useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { BlockNoteEditor } from "@/components/blocknote/BlockNoteEditor";
import { SegmentedControl } from "@/design-system";
import { AuthContext } from "@/features/auth/AuthContext";

import {
  COMMITMENT_VISIBILITY_OPTIONS,
  resolveAssigneeForVisibility,
  reviewerCollidesWithAssignee,
  shouldClearReviewerForVisibility,
} from "./commitmentVisibility";
import { CreateTagModal } from "./CreateTagModal";
import { SearchableSelect } from "./SearchableSelect";
import {
  useAccountsPickerQuery,
  useCategoriesPickerQuery,
  useCreateTagMutation,
  useMembersPickerQuery,
  usePlanYearPickerQuery,
  useTagsPickerQuery,
} from "./hooks";
import type { CommitmentFormErrors } from "./commitmentFormValidation";
import type {
  CommitmentFormValues,
  CommitmentFormVisibility,
} from "./types";


// Mirror the Playbook Commitment authoring form's segmented controls
// so the two surfaces stay visually consistent. Same options, same
// component, same testid scheme.
const COMPLEXITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "1", label: "S" },
  { value: "2", label: "M" },
  { value: "3", label: "L" },
  { value: "5", label: "XL" },
];


const PRIORITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];


export interface CommitmentModalFormProps {
  values: CommitmentFormValues;
  errors: CommitmentFormErrors;
  onChange: <K extends keyof CommitmentFormValues>(
    key: K, value: CommitmentFormValues[K],
  ) => void;
  serverError?: string | null;
}


export function CommitmentModalForm({
  values, errors, onChange, serverError,
}: CommitmentModalFormProps) {
  const accountsQuery = useAccountsPickerQuery();
  const membersQuery = useMembersPickerQuery();
  const tagsQuery = useTagsPickerQuery();
  const categoriesQuery = useCategoriesPickerQuery();
  const createTagMutation = useCreateTagMutation();
  const planYearsQuery = usePlanYearPickerQuery(
    values.account_id || null,
    Boolean(values.account_id),
  );
  const auth = useContext(AuthContext);
  const currentUserId = auth?.user?.id ?? null;
  const isPrivate = values.visibility === "private";

  const [createTagOpen, setCreateTagOpen] = useState(false);

  function handleVisibilityChange(next: CommitmentFormVisibility): void {
    onChange("visibility", next);
    const assigneePatch = resolveAssigneeForVisibility({
      nextVisibility: next,
      currentAssignedTo: values.assigned_to,
      currentUserId,
    });
    if (assigneePatch) {
      onChange("assigned_to", assigneePatch.assigned_to);
      onChange("assigned_job_title", assigneePatch.assigned_job_title);
      onChange(
        "assigned_client_contact_id",
        assigneePatch.assigned_client_contact_id,
      );
    }
    // Private Commitments never carry a reviewer — wipe local state so
    // the BE doesn't have to clean up after the form, and the picker
    // shows "No reviewer" the moment the user picks Private.
    if (shouldClearReviewerForVisibility(next) && values.reviewer_id) {
      onChange("reviewer_id", "");
    }
  }

  function handleAssigneeChange(nextAssignee: string): void {
    onChange("assigned_to", nextAssignee);
    if (nextAssignee) {
      onChange("assigned_job_title", "");
      onChange("assigned_client_contact_id", "");
    }
    // Team-visible Commitments cannot share assignee + reviewer.
    // Clear the reviewer when the new assignee would collide so the
    // BE invariant holds and the user gets immediate feedback.
    if (
      reviewerCollidesWithAssignee({
        assignedTo: nextAssignee,
        reviewerId: values.reviewer_id,
      })
    ) {
      onChange("reviewer_id", "");
    }
  }

  const selectedTagIds = useMemo(() => {
    const slugs = values.tag_slugs
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (slugs.length === 0) return [];
    const tags = tagsQuery.data?.tags ?? [];
    const out: string[] = [];
    for (const slug of slugs) {
      const found = tags.find((t) => t.slug === slug || t.id === slug);
      if (found) out.push(found.id);
    }
    return out;
  }, [values.tag_slugs, tagsQuery.data]);

  function pickTag(tagId: string): void {
    const tags = tagsQuery.data?.tags ?? [];
    const tag = tags.find((t) => t.id === tagId);
    if (!tag) return;
    if (selectedTagIds.includes(tagId)) return;
    const nextSlugs = [
      ...values.tag_slugs.split(/[,\s]+/).filter(Boolean),
      tag.slug,
    ];
    onChange("tag_slugs", nextSlugs.join(", "));
  }

  function unpickTag(tagId: string): void {
    const tags = tagsQuery.data?.tags ?? [];
    const tag = tags.find((t) => t.id === tagId);
    if (!tag) return;
    const nextSlugs = values.tag_slugs
      .split(/[,\s]+/)
      .filter(Boolean)
      .filter((s) => s !== tag.slug);
    onChange("tag_slugs", nextSlugs.join(", "));
  }

  const selectedAccount = (accountsQuery.data?.accounts ?? []).find(
    (a) => a.id === values.account_id,
  );
  const selectedAssignee = (membersQuery.data?.members ?? []).find(
    (m) => m.id === values.assigned_to,
  );
  const selectedReviewer = (membersQuery.data?.members ?? []).find(
    (m) => m.id === values.reviewer_id,
  );
  const selectedTagLabels = (tagsQuery.data?.tags ?? [])
    .filter((t) => selectedTagIds.includes(t.id))
    .map((t) => t.display_name);
  const selectedPlanYear = (planYearsQuery.data?.plan_years ?? []).find(
    (p) => p.id === values.plan_year_id,
  );

  return (
    <div
      data-testid="commitment-modal-form"
      className="flex flex-col gap-4 text-sm"
    >
      <Field label="Title" name="title" required error={errors.title}>
        <input
          type="text"
          data-testid="commitment-modal-form-title"
          autoFocus
          value={values.title}
          onChange={(e) => onChange("title", e.target.value)}
          placeholder="Enter title"
          className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
        />
      </Field>

      <Field label="Description" name="description">
        <div data-testid="commitment-modal-form-description">
          <BlockNoteEditor
            documentId={values.description_document_id || null}
            documentKind="commitment_description"
            fallbackText={values.description || null}
            onCreated={(documentId) => {
              onChange("description_document_id", documentId);
            }}
          />
        </div>
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Account" name="account_id" error={errors.account_id}>
          <SearchableSelect
            fieldId="commitment-modal-account"
            value={values.account_id}
            options={[
              { value: "", label: "No account", isClear: true },
              ...((accountsQuery.data?.accounts ?? []).map((a) => ({
                value: a.id,
                label: a.legal_name,
                hint: a.dba_name ?? undefined,
              }))),
            ]}
            triggerDisplay={
              selectedAccount
                ? <span>{selectedAccount.legal_name}</span>
                : <span className="italic text-text-tertiary">Select Account</span>
            }
            onPick={(accountId) => {
              onChange("account_id", accountId);
              if (accountId !== values.account_id) {
                onChange("plan_year_id", "");
              }
            }}
          />
        </Field>

        <Field label="Plan Year" name="plan_year_id">
          <SearchableSelect
            fieldId="commitment-modal-plan-year"
            value={values.plan_year_id}
            disabled={!values.account_id}
            options={[
              { value: "", label: "—", isClear: true },
              ...((planYearsQuery.data?.plan_years ?? []).map((p) => ({
                value: p.id,
                label: planYearLabel(p.effective_start, p.effective_end),
                hint: p.is_current ? "Current" : undefined,
              }))),
            ]}
            triggerDisplay={
              selectedPlanYear
                ? <span>{planYearLabel(
                    selectedPlanYear.effective_start,
                    selectedPlanYear.effective_end,
                  )}</span>
                : <span className="italic text-text-tertiary">
                    {values.account_id ? "Select Plan Year" : "Select Account first"}
                  </span>
            }
            onPick={(planYearId) => onChange("plan_year_id", planYearId)}
          />
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Assignee"
          name="assigned_to"
          error={errors.assigned_to}
          hint={
            isPrivate
              ? "Locked while Private — only the current user can be assigned."
              : undefined
          }
        >
          <SearchableSelect
            fieldId="commitment-modal-assignee"
            value={values.assigned_to}
            disabled={isPrivate}
            options={[
              { value: "", label: "Unassigned", isClear: true },
              ...((membersQuery.data?.members ?? []).map((m) => ({
                value: m.id,
                label: m.display_name || m.email,
                hint: m.email,
              }))),
            ]}
            triggerDisplay={
              selectedAssignee
                ? <span>{selectedAssignee.display_name || selectedAssignee.email}</span>
                : <span className="italic text-text-tertiary">Unassigned</span>
            }
            onPick={(userId) => handleAssigneeChange(userId)}
          />
        </Field>
        <Field
          label="Reviewer"
          name="reviewer_id"
          hint={
            isPrivate
              ? "Private Commitments cannot have reviewers."
              : undefined
          }
        >
          <SearchableSelect
            fieldId="commitment-modal-reviewer"
            value={values.reviewer_id}
            disabled={isPrivate}
            options={[
              { value: "", label: "No reviewer", isClear: true },
              ...((membersQuery.data?.members ?? [])
                // Team-visible: hide the current assignee from the
                // reviewer picker — the BE rejects same-person reviewer.
                .filter((m) => !values.assigned_to || m.id !== values.assigned_to)
                .map((m) => ({
                  value: m.id,
                  label: m.display_name || m.email,
                  hint: m.email,
                }))),
            ]}
            triggerDisplay={
              selectedReviewer
                ? <span>{selectedReviewer.display_name || selectedReviewer.email}</span>
                : <span className="italic text-text-tertiary">No reviewer</span>
            }
            onPick={(userId) => onChange("reviewer_id", userId)}
          />
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Category" name="category_slug" error={errors.category_slug}>
          <select
            data-testid="commitment-modal-form-category"
            value={values.category_slug}
            onChange={(e) => onChange("category_slug", e.target.value)}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          >
            <option value="">Select Category</option>
            {(categoriesQuery.data?.categories ?? []).map((c) => (
              <option key={c.slug} value={c.slug}>{c.display_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Due Date" name="due_date_time">
          <input
            type="datetime-local"
            data-testid="commitment-modal-form-due"
            value={values.due_date_time}
            onChange={(e) => onChange("due_date_time", e.target.value)}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          />
        </Field>
      </div>

      <div
        data-testid="commitment-modal-form-complexity-priority-row"
        className="grid gap-4 md:grid-cols-2"
      >
        <SegmentedControl
          label="Complexity"
          value={values.complexity}
          options={COMPLEXITY_OPTIONS}
          testIdPrefix="commitment-modal-form-complexity"
          onChange={(value) => onChange("complexity", value)}
        />
        <SegmentedControl
          label="Priority"
          value={values.priority}
          options={PRIORITY_OPTIONS}
          testIdPrefix="commitment-modal-form-priority"
          onChange={(value) =>
            onChange(
              "priority",
              value as CommitmentFormValues["priority"],
            )
          }
        />
      </div>

      <Field label="Tags" name="tag_slugs">
        <div className="flex flex-col gap-1">
          <SearchableSelect
            fieldId="commitment-modal-tags"
            multiple
            value={selectedTagIds}
            options={(tagsQuery.data?.tags ?? []).map((t) => ({
              value: t.id,
              label: t.display_name,
              hint: t.is_regulatory ? "Regulatory" : undefined,
            }))}
            triggerDisplay={
              selectedTagLabels.length === 0
                ? <span className="italic text-text-tertiary">Add tags</span>
                : (
                  <span className="flex flex-wrap gap-1">
                    {selectedTagLabels.slice(0, 4).map((label) => (
                      <span
                        key={label}
                        className="inline-flex items-center rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary"
                      >
                        {label}
                      </span>
                    ))}
                    {selectedTagLabels.length > 4 && (
                      <span className="text-[11px] text-text-tertiary">
                        +{selectedTagLabels.length - 4}
                      </span>
                    )}
                  </span>
                )
            }
            onPick={(tagId) => {
              pickTag(tagId);
              return Promise.resolve();
            }}
            onUnpick={(tagId) => {
              unpickTag(tagId);
              return Promise.resolve();
            }}
          />
          <button
            type="button"
            data-testid="commitment-modal-form-create-tag"
            onClick={() => setCreateTagOpen(true)}
            className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-teal hover:underline"
          >
            <Plus size={10} aria-hidden="true" />
            Create tag
          </button>
        </div>
      </Field>

      <Field
        label="Visibility"
        name="visibility"
        hint={
          isPrivate
            ? "Only you can see this Commitment."
            : "Everyone on your team can see this Commitment."
        }
      >
        <select
          data-testid="commitment-modal-form-visibility"
          value={values.visibility}
          onChange={(e) =>
            handleVisibilityChange(
              e.target.value as CommitmentFormVisibility,
            )
          }
          className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary md:max-w-xs"
        >
          {COMMITMENT_VISIBILITY_OPTIONS.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              data-testid={`commitment-modal-form-visibility-option-${opt.value}`}
            >
              {opt.label}
              {opt.hint ? ` — ${opt.hint}` : ""}
            </option>
          ))}
        </select>
      </Field>

      {serverError && (
        <p
          role="alert"
          data-testid="commitment-modal-form-server-error"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
        >
          {serverError}
        </p>
      )}

      <CreateTagModal
        open={createTagOpen}
        isPending={createTagMutation.isPending}
        error={
          createTagMutation.isError
            ? (createTagMutation.error as Error).message
            : null
        }
        onCancel={() => {
          setCreateTagOpen(false);
          createTagMutation.reset();
        }}
        onCreate={({ display_name, is_regulatory }) => {
          createTagMutation.mutate(
            { display_name, is_regulatory },
            {
              onSuccess: (tag) => {
                const existing = values.tag_slugs
                  .split(/[,\s]+/)
                  .map((s) => s.trim())
                  .filter(Boolean);
                if (!existing.includes(tag.slug)) {
                  onChange(
                    "tag_slugs",
                    [...existing, tag.slug].join(", "),
                  );
                }
                setCreateTagOpen(false);
                createTagMutation.reset();
              },
            },
          );
        }}
      />
    </div>
  );
}


function Field({
  label, name, required, error, hint, children,
}: {
  label: string;
  name: string;
  required?: boolean;
  error?: string | undefined;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={`commitment-modal-form-${name}`}
      className="flex flex-col gap-1 text-xs font-medium text-text-secondary"
    >
      <span>
        {label}
        {required && <span className="ml-0.5 text-ember">*</span>}
      </span>
      {children}
      {hint && (
        <span className="text-[11px] font-normal text-text-tertiary">
          {hint}
        </span>
      )}
      {error && (
        <span
          data-testid={`commitment-modal-form-${name}-error`}
          className="text-[11px] font-medium text-ember"
        >
          {error}
        </span>
      )}
    </label>
  );
}


function planYearLabel(start: string | null, end: string | null): string {
  const left = start ? formatYear(start) : "—";
  const right = end ? formatYear(end) : "—";
  return `${left} → ${right}`;
}


function formatYear(iso: string): string {
  // Defensive: BE-052 emits "YYYY-MM-DD"; the picker also accepts
  // full ISO from the saved-detail path. Slice instead of date-parse
  // so we don't burn TZ offsets on a date-only field.
  return iso.slice(0, 10);
}
